import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { useAuth } from '../../context/AuthContext';
import TypeSearch from '../../components/common/TypeSearch';
import SupplierPicker from '../../components/common/SupplierPicker';
import { usePasscode } from '../../components/common/PasscodeModal';
import { postJournal, voidJournal } from '../../api/journal';
import { ACCOUNTS, getSupplierDepositRemaining, payableAccountForSupplierType } from '../../api/accounts';
import { getClientDepositBalance } from '../../api/receiptAllocations';

const SERVICE_TYPES = ['Hotel','Flight','Transfer','Tour','Land Package','Visa','Insurance','Cruise','Other'].map(t => ({ id: t, label: t }));
const COMM_BASIS_OPTIONS = [
  { id: 'ON_GP', label: 'Gross Profit (GP)' },
  { id: 'ON_SELL', label: 'Selling Price' },
  { id: 'ON_COST', label: 'Cost Price' },
];

// A package is view-only (costs locked) by default — unlocking requires the same admin
// passcode used for Confirm/Cancel/Delete elsewhere on this page. Shared between this
// page and PackageForm.js (the name/pax/dates edit page) via sessionStorage, so unlocking
// through either one covers both — "Edit Package" doesn't ask for the passcode twice.
// The unlock is single-use: PackageDetail consumes (clears) it the moment it mounts, so
// coming back here right after editing metadata still shows costs unlocked, but any later,
// separate visit to this package starts locked again — it must never stay unlocked for
// the rest of the browser session just because it was unlocked once before.
export function isPackageUnlocked(id) {
  try { return JSON.parse(sessionStorage.getItem('unlockedPackages') || '[]').includes(id); } catch { return false; }
}
export function markPackageUnlocked(id) {
  try {
    const set = new Set(JSON.parse(sessionStorage.getItem('unlockedPackages') || '[]'));
    set.add(id);
    sessionStorage.setItem('unlockedPackages', JSON.stringify([...set]));
  } catch { /* sessionStorage unavailable — worst case, unlock doesn't persist across pages */ }
}
export function clearPackageUnlocked(id) {
  try {
    const set = new Set(JSON.parse(sessionStorage.getItem('unlockedPackages') || '[]'));
    set.delete(id);
    sessionStorage.setItem('unlockedPackages', JSON.stringify([...set]));
  } catch { /* no-op */ }
}

export default function PackageDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const username = user?.email?.split('@')[0] || 'unknown';
  const canSeePnl = role === 'sadmin' || role === 'admin';
  const { prompt: promptPasscode, PasscodeModal } = usePasscode();
  const [unlocked, setUnlocked] = useState(() => isPackageUnlocked(id));
  // Consume the unlock token exactly once, right after mount — this is what makes the
  // unlock single-use per visit instead of sticking unlocked for the whole browser
  // session (the id dependency means a fresh mount from navigating to a different
  // package, or back to this one later, re-checks and re-clears independently).
  useEffect(() => {
    if (isPackageUnlocked(id)) clearPackageUnlocked(id);
  }, [id]);
  const unlockCosts = async () => {
    try { await promptPasscode(); } catch { return; }
    markPackageUnlocked(id);
    setUnlocked(true);
  };

  const [pkg, setPkg] = useState(null);
  const [packageSuppliers, setPackageSuppliers] = useState([]);
  const [sellingPrices, setSellingPrices] = useState([]);
  const [allSuppliers, setAllSuppliers] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [tab, setTab] = useState('suppliers');
  // Admin-manageable instead of hardcoded — new categories (e.g. "Child With Extra Bed")
  // can be added right from this screen without a code change. PAX_TYPES is for real
  // passenger categories (line items / selling price); COST_PAX_TYPES adds the
  // cost-only "Common (All Pax)"-style entries for genuinely shared costs.
  const [paxTypes, setPaxTypes] = useState([]);
  const [newPaxTypeName, setNewPaxTypeName] = useState('');
  const [addingPaxType, setAddingPaxType] = useState(false);
  const PAX_TYPES = paxTypes.filter(t => !t.is_common).map(t => t.name);
  const COST_PAX_TYPES = paxTypes.map(t => t.name);

  const loadPaxTypes = useCallback(() => {
    supabase.from('pax_types').select('id, name, is_common').eq('is_active', true).order('sort_order').then(({ data }) => setPaxTypes(data || []));
  }, []);
  const addPaxType = async (isCommon) => {
    const name = newPaxTypeName.trim();
    if (!name) return;
    setAddingPaxType(true);
    // `name` has a DB-level unique constraint that ignores is_active — a previously
    // removed (soft-deleted) pax type still occupies its name, so re-adding it must
    // revive that row rather than insert a fresh one, or it fails on the unique
    // constraint with no visible feedback (looked like "Add" just did nothing).
    const { data: existing } = await supabase.from('pax_types').select('id, is_active').eq('name', name).maybeSingle();
    let err;
    if (existing) {
      if (existing.is_active) {
        setError(`"${name}" already exists.`);
        setAddingPaxType(false);
        return;
      }
      ({ error: err } = await supabase.from('pax_types').update({ is_common: isCommon, sort_order: paxTypes.length, is_active: true, created_by: username }).eq('id', existing.id));
    } else {
      ({ error: err } = await supabase.from('pax_types').insert({ name, is_common: isCommon, sort_order: paxTypes.length, created_by: username }));
    }
    if (!err) { setNewPaxTypeName(''); loadPaxTypes(); }
    else setError(err.message);
    setAddingPaxType(false);
  };
  // Soft-delete (is_active=false) rather than a real delete — same convention as
  // currencies/suppliers elsewhere. Any existing rate that already used this name keeps
  // showing it fine (it's just a text value on that row); this only removes it from
  // future dropdowns, so it's always safe even if you're not sure whether it's in use.
  const removePaxType = async (id) => {
    await supabase.from('pax_types').update({ is_active: false }).eq('id', id);
    loadPaxTypes();
  };

  // Rates inside a supplier's box follow the admin-set pax type order (Adult, Child With
  // Bed, Child Without Bed, Infant, …) rather than whichever was entered first.
  const paxTypeRank = (t) => { const i = paxTypes.findIndex(p => p.name === (t || 'Adult')); return i < 0 ? 999 : i; };
  const [sForm, setSForm] = useState({ cost_type: 'supplier', supplier_id: '', supplier_name: '', service_type: '', description: '', cost_per_pax: '', currency_id: '', currency_code: '', budgeted_fx_rate: '1', non_refundable_deposit: '', deposit_currency_id: '', deposit_currency_code: '', notes: '', is_included: false, paid_from_account_id: '', chargeToSupplierBalance: false, pax_type: 'Adult' });
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [rForm, setRForm] = useState({ package_supplier_id: '', supplier_type: '', cost_per_pax: '', currency_id: '', currency_code: '', budgeted_fx_rate: '1', non_refundable_deposit: '', deposit_currency_id: '', deposit_currency_code: '', notes: '', is_included: false, pax_type: 'Adult' });
  const [pForm, setPForm] = useState({ pax_from: '1', pax_to: '99', sell_price_per_pax: '', currency_id: '', currency_code: '', pax_type: 'Adult' });
  const [commForm, setCommForm] = useState({ commission_percent: '', commission_basis: 'ON_GP', commission_basis_label: 'Gross Profit (GP)' });
  const [commType, setCommType] = useState('PERCENT');
  const [commTiers, setCommTiers] = useState([]);
  const [tierForm, setTierForm] = useState({ gp_from: '', gp_to: '', commission_amount: '' });
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');
  const [editRate, setEditRate] = useState(null);
  const [deletingId, setDeletingId] = useState('');
  const [cancelReview, setCancelReview] = useState(null);
  const [cancelSaving, setCancelSaving] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [refundFromAccountId, setRefundFromAccountId] = useState('');

  // Invoices raised for this package: linked directly (invoices.package_id — Group package
  // invoices raised from New Invoice) or through one of the package's bookings.
  const [packageInvoices, setPackageInvoices] = useState([]);
  const [linkInvoiceNo, setLinkInvoiceNo] = useState('');
  const loadInvoices = useCallback(async () => {
    const { data: bks } = await supabase.from('bookings').select('id').eq('package_id', id);
    const bookingIds = (bks || []).map(b => b.id);
    const cols = 'id, invoice_number, issued_date, status, package_id, line_items, clients(full_name, client_code)';
    const [{ data: direct }, { data: viaBooking }] = await Promise.all([
      supabase.from('invoices').select(cols).eq('package_id', id),
      bookingIds.length
        ? supabase.from('invoices').select(cols).eq('source_type', 'booking').in('source_id', bookingIds)
        : Promise.resolve({ data: [] }),
    ]);
    const byId = new Map([...(direct || []), ...(viaBooking || [])].map(i => [i.id, i]));
    setPackageInvoices([...byId.values()].sort((a, b) =>
      ((a.status === 'VOID') - (b.status === 'VOID')) || (b.issued_date || '').localeCompare(a.issued_date || '')));
  }, [id]);

  const linkInvoice = async () => {
    const num = linkInvoiceNo.trim().toUpperCase();
    if (!num) return;
    const { data: inv } = await supabase.from('invoices').select('id, package_id').eq('invoice_number', num).maybeSingle();
    if (!inv) { setError(`Invoice ${num} not found.`); return; }
    if (inv.package_id && inv.package_id !== id && !window.confirm(`${num} is already linked to another package. Move it to this one?`)) return;
    const { error: err } = await supabase.from('invoices').update({ package_id: id }).eq('id', inv.id);
    if (err) { setError(err.message); return; }
    setLinkInvoiceNo('');
    loadInvoices();
  };
  const unlinkInvoice = async (inv) => {
    if (!window.confirm(`Remove ${inv.invoice_number} from this package? The invoice itself is not changed.`)) return;
    await supabase.from('invoices').update({ package_id: null }).eq('id', inv.id);
    loadInvoices();
  };

  const loadData = useCallback(async () => {
    const [{ data: p }, { data: ps }, { data: sp }, { data: ct }] = await Promise.all([
      supabase.from('packages').select('*').eq('id', id).single(),
      supabase.from('package_suppliers').select('*, suppliers(name, supplier_type), package_supplier_rates(*, currency:currency_id(code), deposit_currency:deposit_currency_id(code))').eq('package_id', id).order('created_at'),
      supabase.from('package_selling_prices').select('*, currencies(code)').eq('package_id', id).order('pax_from'),
      supabase.from('package_commission_tiers').select('*').eq('package_id', id).order('gp_from'),
    ]);
    setPkg(p);
    loadInvoices();
    setPackageSuppliers(ps || []);
    setSellingPrices(sp || []);
    setCommTiers(ct || []);
    if (p) {
      const basisLabel = COMM_BASIS_OPTIONS.find(o => o.id === (p.commission_basis || 'ON_GP'))?.label || 'Gross Profit (GP)';
      setCommForm({ commission_percent: p.commission_percent || '', commission_basis: p.commission_basis || 'ON_GP', commission_basis_label: basisLabel });
      setCommType(p.commission_type || 'PERCENT');
    }
  }, [id, loadInvoices]);

  useEffect(() => {
    loadData();
    supabase.from('suppliers').select('id, name, supplier_type').eq('is_active', true).order('name').then(({ data }) => setAllSuppliers(data || []));
    supabase.from('currencies').select('id, code').eq('is_active', true).order('code').then(({ data }) => setCurrencies(data || []));
    supabase.from('chart_of_accounts').select('id, account_code, account_name').eq('is_active', true).eq('is_payment_source', true).order('account_code').then(({ data }) => setPaymentAccounts(data || []));
    loadPaxTypes();
  }, [loadData, loadPaxTypes]);

  // A supplier can now carry a separate rate row per pax_type (Adult/Child With Bed/
  // Child Without Bed/Infant) — this per-pax budget figure is one representative
  // "per person" number for the summary/commission calcs below, so it always resolves
  // to the Adult rate specifically (falling back to whichever rate exists for packages
  // created before pax_type existed).
  const totalCostPerPaxLKR = packageSuppliers.reduce((s, ps) => {
    const r = ps.package_supplier_rates?.find(x => x.pax_type === 'Adult') || ps.package_supplier_rates?.[0];
    if (!r || r.is_included) return s;
    return s + parseFloat(r.cost_per_pax || 0) * parseFloat(r.budgeted_fx_rate || 1);
  }, 0);

  const [pnlLoading, setPnlLoading] = useState(false);
  const [pnlData, setPnlData] = useState(null);

  // Read-only reference report — actual revenue/cost pulled straight from the ledger for
  // every booking under this package, as things stand today. Cost is summed from
  // BOOKING_COST/BOOKING_COMMISSION only (the current, correct per-booking cost path —
  // see confirmPackage()'s note above: package confirmation itself posts no cost).
  // Deliberately excludes any legacy reference_type='package' commitment entries some older
  // packages still carry from before that double-counting bug was fixed — including them
  // here would just reproduce the same bug in a new place.
  const loadPnl = useCallback(async () => {
    setPnlLoading(true);
    try {
      const { data: bookings } = await supabase.from('bookings')
        .select('id, num_pax, booking_status, client_id, booking_ref, clients(full_name)')
        .eq('package_id', id);
      const bookingIds = (bookings || []).map(b => b.id);

      const { data: invoices } = bookingIds.length
        ? await supabase.from('invoices').select('id, invoice_number, source_id, status').eq('source_type', 'booking').in('source_id', bookingIds).neq('status', 'VOID')
        : { data: [] };
      const invoiceByBooking = new Map((invoices || []).map(i => [i.source_id, i]));
      const invoiceIds = (invoices || []).map(i => i.id);

      const [{ data: costLines }, { data: revLines }] = await Promise.all([
        bookingIds.length
          ? supabase.from('journal_lines')
              .select('debit_amount, credit_amount, account:account_id(account_types(category)), journal_entries!inner(reference_id, reference_type, status, is_reversed, reversal_of)')
              .in('journal_entries.reference_type', ['BOOKING_COST', 'BOOKING_COMMISSION'])
              .in('journal_entries.reference_id', bookingIds)
              .eq('journal_entries.status', 'POSTED').eq('journal_entries.is_reversed', false).is('journal_entries.reversal_of', null)
          : Promise.resolve({ data: [] }),
        invoiceIds.length
          ? supabase.from('journal_lines')
              .select('debit_amount, credit_amount, account:account_id(account_types(category)), journal_entries!inner(reference_id, reference_type, status, is_reversed, reversal_of)')
              .in('journal_entries.reference_type', ['INVOICE_TICKET', 'INVOICE_VISA', 'INVOICE_BOOKING', 'invoice'])
              .in('journal_entries.reference_id', invoiceIds)
              .eq('journal_entries.status', 'POSTED').eq('journal_entries.is_reversed', false).is('journal_entries.reversal_of', null)
          : Promise.resolve({ data: [] }),
      ]);

      const costByBooking = {};
      (costLines || []).forEach(l => {
        if (l.account?.account_types?.category !== 'EXPENSE') return;
        const bId = l.journal_entries.reference_id;
        costByBooking[bId] = (costByBooking[bId] || 0) + (parseFloat(l.debit_amount) || 0) - (parseFloat(l.credit_amount) || 0);
      });
      const revByInvoice = {};
      (revLines || []).forEach(l => {
        if (l.account?.account_types?.category !== 'INCOME') return;
        const invId = l.journal_entries.reference_id;
        revByInvoice[invId] = (revByInvoice[invId] || 0) + (parseFloat(l.credit_amount) || 0) - (parseFloat(l.debit_amount) || 0);
      });

      const rows = (bookings || []).map(b => {
        const inv = invoiceByBooking.get(b.id);
        return {
          booking_id: b.id,
          booking_ref: b.booking_ref,
          client_name: b.clients?.full_name || '—',
          num_pax: b.num_pax || 0,
          status: b.booking_status,
          invoice_number: inv?.invoice_number || null,
          invoice_id: inv?.id || null,
          revenue: inv ? (revByInvoice[inv.id] || 0) : 0,
          cost: costByBooking[b.id] || 0,
        };
      });

      const actualRevenue = rows.reduce((s, r) => s + r.revenue, 0);
      const actualCost = rows.reduce((s, r) => s + r.cost, 0);
      const totalPax = (bookings || []).filter(b => b.booking_status !== 'CANCELLED').reduce((s, b) => s + (b.num_pax || 0), 0);

      const budgetedCost = totalCostPerPaxLKR * totalPax;
      const matchedPrice = totalPax
        ? sellingPrices.find(sp => sp.pax_type === 'Adult' && totalPax >= sp.pax_from && totalPax <= sp.pax_to)
        : null;
      const budgetedRevenue = matchedPrice ? totalPax * parseFloat(matchedPrice.sell_price_per_pax) : null;

      setPnlData({ rows, actualRevenue, actualCost, totalPax, budgetedCost, budgetedRevenue, budgetedCurrency: matchedPrice?.currencies?.code || null });
    } finally {
      setPnlLoading(false);
    }
  }, [id, sellingPrices, totalCostPerPaxLKR]);

  const addSupplier = async () => {
    const isInternal = sForm.cost_type === 'internal';
    if (isInternal) {
      if (!sForm.description) return setError('Enter a description for this internal cost');
    } else if (!sForm.supplier_id) {
      return setError('Select a supplier');
    }
    if (!sForm.cost_per_pax || !sForm.currency_id) return setError('Cost per pax and currency are required');
    const isAirlineForm = allSuppliers.find(s => s.id === sForm.supplier_id)?.supplier_type === 'Airline' || sForm.service_type === 'Flight';
    if (isAirlineForm && parseFloat(sForm.non_refundable_deposit) > 0) {
      // The deposit is journalled in its own currency. With no rate field for it, the only safe
      // cases are: same currency as the cost (uses that rate) or LKR (rate 1). Anything else
      // would silently reuse the cost currency's rate and misstate the amount.
      const sameCcy = !sForm.deposit_currency_id || sForm.deposit_currency_id === sForm.currency_id;
      if (!sameCcy && sForm.deposit_currency_code !== 'LKR') {
        return setError('Enter the deposit in the same currency as the cost, or in LKR.');
      }
    }
    setSaving('supplier'); setError('');
    // One box per supplier: a supplier already on this package (e.g. Oscar's Adult rate)
    // gets this rate (e.g. Child Without Bed) added under its existing box instead of a
    // second, separate box for the same supplier.
    const existing = !isInternal && packageSuppliers.find(p => p.cost_type !== 'internal' && p.supplier_id === sForm.supplier_id);
    if (existing && (existing.package_supplier_rates || []).some(r => (r.pax_type || 'Adult') === (sForm.pax_type || 'Adult'))) {
      setSaving('');
      return setError(`${existing.suppliers?.name || 'This supplier'} already has a ${sForm.pax_type || 'Adult'} rate — edit that rate instead`);
    }
    let ps = existing;
    if (!ps) {
      const { data: created, error: err } = await supabase.from('package_suppliers').insert({
        package_id: id, supplier_id: isInternal ? null : sForm.supplier_id, cost_type: sForm.cost_type,
        service_type: sForm.service_type, description: sForm.description, created_by: username,
      }).select().single();
      if (err) { setError(err.message); setSaving(''); return; }
      ps = created;
    }
    const selectedSupplier = allSuppliers.find(s => s.id === sForm.supplier_id);
    // Internal costs have no deposit concept — the whole amount defers to Confirm Package,
    // same as any non-airline supplier cost today.
    const isAir = !isInternal && (selectedSupplier?.supplier_type === 'Airline' || sForm.service_type?.toLowerCase().includes('flight'));
    const { data: rateRow } = await supabase.from('package_supplier_rates').insert({
      package_supplier_id: ps.id,
      cost_per_pax: parseFloat(sForm.cost_per_pax),
      currency_id: sForm.currency_id,
      budgeted_fx_rate: sForm.currency_code === 'LKR' ? 1 : (parseFloat(sForm.budgeted_fx_rate) || 1),
      non_refundable_deposit: isAir ? (parseFloat(sForm.non_refundable_deposit) || 0) : 0,
      deposit_currency_id: sForm.deposit_currency_id || null,
      notes: sForm.notes,
      is_included: sForm.is_included,
      pax_type: sForm.pax_type || 'Adult',
      valid_from: new Date().toISOString().slice(0, 10),
      created_by: username,
    }).select().single();
    // An airline deposit is a PAYABLE, not a payment: saving the cost only records that we owe it
    // (DR Supplier Deposits prepaid asset / CR the supplier's payable, tagged to them). The bank
    // moves only when someone actually pays it, through a Payment Voucher. Posting a bank credit
    // here instead double-counted every deposit later paid for real, and trusted whatever was in
    // the FX field (an LKR deposit once went in at rate 375 = LKR 142.5M out of the bank).
    // It is NOT an expense — it only becomes one if forfeited (package cancelled) or consumed
    // by real ticket costs (see BookingForm.js postBookingJournals).
    const depositAmt = isAir ? (parseFloat(sForm.non_refundable_deposit) || 0) : 0;
    if (depositAmt > 0) {
      const depCode = sForm.deposit_currency_code || sForm.currency_code;
      // depositAmt stays in its own currency — postJournal() applies the rate once. LKR is always 1.
      const exchRate = depCode === 'LKR' ? 1 : (parseFloat(sForm.budgeted_fx_rate) || 1);
      const desc = `${pkg?.package_code} — Advance payable — ${sForm.description || selectedSupplier?.name || ''}`;
      const je = await postJournal({
        date: new Date().toISOString().slice(0, 10),
        description: `Airline advance payable — ${pkg?.package_code} — ${selectedSupplier?.name}`,
        reference_type: 'package',
        reference_id: id,
        currency_id: sForm.deposit_currency_id || sForm.currency_id || null,
        exchange_rate: exchRate,
        lines: [
          { account_id: ACCOUNTS.SUPPLIER_DEPOSITS, debit_amount: depositAmt, credit_amount: 0, description: desc, party_type: 'supplier', party_id: sForm.supplier_id },
          { account_id: payableAccountForSupplierType(selectedSupplier?.supplier_type), debit_amount: 0, credit_amount: depositAmt, description: desc, party_type: 'supplier', party_id: sForm.supplier_id },
        ],
        created_by: username,
      });
      if (rateRow?.id) await supabase.from('package_supplier_rates').update({ deposit_journal_entry_id: je.id }).eq('id', rateRow.id);
    }
    setSForm({ cost_type: 'supplier', supplier_id: '', supplier_name: '', service_type: '', description: '', cost_per_pax: '', currency_id: '', currency_code: '', budgeted_fx_rate: '1', non_refundable_deposit: '', deposit_currency_id: '', deposit_currency_code: '', notes: '', is_included: false, paid_from_account_id: '', chargeToSupplierBalance: false, pax_type: 'Adult' });
    loadData();
    setSaving('');
  };

  const addRate = async () => {
    if (!rForm.package_supplier_id || !rForm.cost_per_pax || !rForm.currency_id) return setError('Supplier, cost and currency required');
    setSaving('rate'); setError('');
    const { error: err } = await supabase.from('package_supplier_rates').insert({
      package_supplier_id: rForm.package_supplier_id,
      cost_per_pax: parseFloat(rForm.cost_per_pax),
      currency_id: rForm.currency_id,
      budgeted_fx_rate: parseFloat(rForm.budgeted_fx_rate) || 1,
      non_refundable_deposit: parseFloat(rForm.non_refundable_deposit) || 0,
      deposit_currency_id: rForm.deposit_currency_id || null,
      notes: rForm.notes,
      is_included: rForm.is_included,
      pax_type: rForm.pax_type || 'Adult',
      valid_from: new Date().toISOString().slice(0, 10),
      created_by: username,
    });
    if (err) setError(err.message);
    else { setRForm(f => ({ ...f, cost_per_pax: '', non_refundable_deposit: '', notes: '', is_included: false, package_supplier_id: '', pax_type: 'Adult' })); loadData(); }
    setSaving('');
  };

  const addSellingPrice = async () => {
    if (!pForm.sell_price_per_pax || !pForm.currency_id) return setError('Price and currency required');
    setSaving('price'); setError('');
    const { error: err } = await supabase.from('package_selling_prices').insert({
      package_id: id,
      pax_from: parseInt(pForm.pax_from) || 1,
      pax_to: parseInt(pForm.pax_to) || 99,
      sell_price_per_pax: parseFloat(pForm.sell_price_per_pax),
      currency_id: pForm.currency_id,
      pax_type: pForm.pax_type || 'Adult',
      valid_from: new Date().toISOString().slice(0, 10),
      created_by: username,
    });
    if (err) setError(err.message);
    else { setPForm(f => ({ ...f, sell_price_per_pax: '' })); loadData(); }
    setSaving('');
  };

  const deleteSellingPrice = async (priceId) => {
    if (!window.confirm('Delete this selling price tier?')) return;
    setDeletingId(priceId);
    await supabase.from('package_selling_prices').delete().eq('id', priceId);
    setDeletingId('');
    loadData();
  };

  const toggleIncluded = async (rateId, currentVal) => {
    await supabase.from('package_supplier_rates').update({ is_included: !currentVal }).eq('id', rateId);
    loadData();
  };

  const deleteSupplier = async (psId) => {
    if (!window.confirm('Delete this supplier and all its rates? Any deposit payable recorded for it will be reversed.')) return;
    setDeletingId(psId);
    const { data: rs } = await supabase.from('package_supplier_rates').select('deposit_journal_entry_id').eq('package_supplier_id', psId);
    for (const r of rs || []) if (r.deposit_journal_entry_id) await voidJournal(r.deposit_journal_entry_id, 'Supplier removed from package', username);
    await supabase.from('package_supplier_rates').delete().eq('package_supplier_id', psId);
    await supabase.from('package_suppliers').delete().eq('id', psId);
    setDeletingId('');
    loadData();
  };

  const deleteRate = async (rateId) => {
    if (!window.confirm('Delete this rate? Any deposit payable recorded for it will be reversed.')) return;
    setDeletingId(rateId);
    const { data: rt } = await supabase.from('package_supplier_rates').select('deposit_journal_entry_id').eq('id', rateId).single();
    if (rt?.deposit_journal_entry_id) await voidJournal(rt.deposit_journal_entry_id, 'Rate removed from package', username);
    await supabase.from('package_supplier_rates').delete().eq('id', rateId);
    setDeletingId('');
    loadData();
  };

  const saveEditRate = async () => {
    if (!editRate.cost_per_pax || !editRate.currency_id) return setError('Cost and currency required');
    setSaving('editRate');
    const { error: err } = await supabase.from('package_supplier_rates').update({
      cost_per_pax: parseFloat(editRate.cost_per_pax),
      currency_id: editRate.currency_id,
      budgeted_fx_rate: parseFloat(editRate.budgeted_fx_rate) || 1,
      notes: editRate.notes,
      is_included: editRate.is_included,
    }).eq('id', editRate.id);
    if (err) setError(err.message);
    else { setEditRate(null); loadData(); }
    setSaving('');
  };

  const saveCommission = async () => {
    setSaving('comm'); setError('');
    const { error: err } = await supabase.from('packages').update({
      commission_percent: parseFloat(commForm.commission_percent) || 0,
      commission_basis: commForm.commission_basis,
      commission_type: commType,
    }).eq('id', id);
    if (err) setError(err.message);
    else loadData();
    setSaving('');
  };

  const addCommTier = async () => {
    if (tierForm.commission_amount === '') return setError('Commission amount required');
    setSaving('tier'); setError('');
    const { error: err } = await supabase.from('package_commission_tiers').insert({
      package_id: id,
      gp_from: parseFloat(tierForm.gp_from) || 0,
      gp_to: tierForm.gp_to !== '' ? parseFloat(tierForm.gp_to) : null,
      commission_amount: parseFloat(tierForm.commission_amount) || 0,
    });
    if (err) setError(err.message);
    else { setTierForm({ gp_from: '', gp_to: '', commission_amount: '' }); loadData(); }
    setSaving('');
  };

  const deleteCommTier = async (tierId) => {
    await supabase.from('package_commission_tiers').delete().eq('id', tierId);
    loadData();
  };

  // Confirming a package locks in rates so it can be booked — it does NOT post any
  // bulk supplier cost. Supplier cost is posted per-pax as each individual booking is
  // confirmed (see BookingForm.js postBookingJournals), offset against any advance
  // already paid (see addSupplier above). Posting the full min_pax cost here used to
  // double-count against those per-booking postings — see api/journal.js header notes.
  const confirmPackage = async () => {
    try { await promptPasscode(); } catch { return; }
    if (!window.confirm('Confirm this package? It becomes bookable and rates are locked. Supplier costs post as clients actually book.')) return;
    await supabase.from('packages').update({ confirmed: true, confirmed_at: new Date().toISOString(), confirmed_by: username }).eq('id', id);
    loadData();
  };

  // Loads a read-only preview of everything a package cancellation will touch: every
  // active booking (and its client), each unique client's unapplied deposit balance,
  // and each unique supplier's remaining unconsumed advance for this package. Nothing
  // is written yet — the admin reviews/edits refund vs. forfeit per client before
  // confirmCancelPackage() actually posts anything.
  const openCancelReview = async () => {
    try { await promptPasscode(); } catch { return; }
    const { data: bookings } = await supabase.from('bookings')
      .select('id, booking_ref, client_id, clients(full_name)')
      .eq('package_id', id).neq('booking_status', 'CANCELLED');

    const clientIds = [...new Set((bookings || []).map(b => b.client_id).filter(Boolean))];
    const clientRows = {};
    for (const cid of clientIds) {
      const balance = await getClientDepositBalance(cid);
      const client = (bookings || []).find(b => b.client_id === cid)?.clients;
      clientRows[cid] = { client_name: client?.full_name || 'Client', balance, refund: '0', forfeit: '0' };
    }

    const supplierIds = [...new Set((packageSuppliers || []).map(ps => ps.supplier_id).filter(Boolean))];
    const supplierWriteoffs = [];
    for (const sid of supplierIds) {
      const remaining = await getSupplierDepositRemaining(id, sid);
      if (remaining > 0.01) {
        const supplier = packageSuppliers.find(ps => ps.supplier_id === sid)?.suppliers;
        supplierWriteoffs.push({ supplier_id: sid, supplier_name: supplier?.name || 'Supplier', remaining_deposit: remaining });
      }
    }

    setCancelReason('');
    setRefundFromAccountId('');
    setCancelReview({ bookings: bookings || [], clientRows, supplierWriteoffs });
  };

  const cancelReviewHasRefund = () => cancelReview && Object.values(cancelReview.clientRows).some(r => parseFloat(r.refund) > 0);
  const cancelReviewInvalid = () => {
    if (!cancelReview) return true;
    if (!cancelReason.trim()) return true;
    if (cancelReviewHasRefund() && !refundFromAccountId) return true;
    return Object.values(cancelReview.clientRows).some(r => {
      const refund = parseFloat(r.refund) || 0;
      const forfeit = parseFloat(r.forfeit) || 0;
      return refund < 0 || forfeit < 0 || (refund + forfeit) > r.balance + 0.01;
    });
  };

  // The full write sequence: write off remaining supplier advances, reverse every
  // affected booking (and its commission), void any invoice generated from those
  // bookings, settle each client's deposit (refund / forfeit / leave as credit), then
  // flag the package itself cancelled. Nothing is deleted — everything is reversed or
  // flagged, per the project's never-delete accounting rule.
  const confirmCancelPackage = async () => {
    if (cancelReviewInvalid()) return;
    setCancelSaving(true);
    const today = new Date().toISOString().slice(0, 10);
    try {
      // 1. Write off remaining supplier advances
      for (const w of cancelReview.supplierWriteoffs) {
        await postJournal({
          date: today,
          description: `Package cancelled — advance forfeited — ${pkg.package_code} — ${w.supplier_name}`,
          reference_type: 'PACKAGE_CANCEL_NRD',
          reference_id: id,
          exchange_rate: 1,
          lines: [
            { account_id: ACCOUNTS.NRD_LOSS, debit_amount: w.remaining_deposit, credit_amount: 0, description: 'NRD/advance forfeited on package cancellation' },
            { account_id: ACCOUNTS.SUPPLIER_DEPOSITS, debit_amount: 0, credit_amount: w.remaining_deposit, description: 'Advance written off', party_type: 'supplier', party_id: w.supplier_id },
          ],
          created_by: username,
        });
      }

      // 2. Reverse every active booking's journals and mark it cancelled
      const bookingIds = cancelReview.bookings.map(b => b.id);
      for (const booking of cancelReview.bookings) {
        const { data: jes } = await supabase.from('journal_entries').select('id')
          .in('reference_type', ['BOOKING', 'BOOKING_COST', 'BOOKING_COMMISSION'])
          .eq('reference_id', booking.id).eq('status', 'POSTED').eq('is_reversed', false);
        for (const je of jes || []) await voidJournal(je.id, `Package ${pkg.package_code} cancelled: ${cancelReason}`, username);
        await supabase.from('bookings').update({
          booking_status: 'CANCELLED', cancelled_by: username, cancelled_at: new Date().toISOString(), cancel_reason: `Package cancelled: ${cancelReason}`,
        }).eq('id', booking.id);
      }

      // 3. Void any invoice generated from those bookings (same pattern as InvoicePrint.js's void action)
      const { data: invoices } = await supabase.from('invoices')
        .select('id, journal_entry_id').eq('source_type', 'booking').in('source_id', bookingIds).neq('status', 'VOID');
      for (const inv of invoices || []) {
        const { data: journals } = await supabase.from('journal_entries').select('id, is_reversed')
          .ilike('reference_type', 'invoice%').eq('reference_id', inv.id);
        for (const je of journals || []) {
          if (!je.is_reversed) await voidJournal(je.id, `Package ${pkg.package_code} cancelled`, username);
        }
        if (inv.journal_entry_id) {
          const { data: je } = await supabase.from('journal_entries').select('is_reversed').eq('id', inv.journal_entry_id).single();
          if (je && !je.is_reversed) await voidJournal(inv.journal_entry_id, `Package ${pkg.package_code} cancelled`, username);
        }
        await supabase.from('invoices').update({ status: 'VOID' }).eq('id', inv.id);
      }

      // 4. Settle each client's deposit balance
      for (const [clientId, row] of Object.entries(cancelReview.clientRows)) {
        const refund = parseFloat(row.refund) || 0;
        const forfeit = parseFloat(row.forfeit) || 0;
        if (refund > 0) {
          await postJournal({
            date: today,
            description: `Deposit refund — ${pkg.package_code} — ${row.client_name}`,
            reference_type: 'PACKAGE_CANCEL_DEPOSIT_REFUND',
            reference_id: id,
            exchange_rate: 1,
            lines: [
              { account_id: ACCOUNTS.CLIENT_DEPOSITS, debit_amount: refund, credit_amount: 0, description: 'Deposit refunded', party_type: 'client', party_id: clientId },
              { account_id: refundFromAccountId, debit_amount: 0, credit_amount: refund, description: 'Refund paid out' },
            ],
            created_by: username,
          });
        }
        if (forfeit > 0) {
          await postJournal({
            date: today,
            description: `Deposit forfeited — ${pkg.package_code} — ${row.client_name}`,
            reference_type: 'PACKAGE_CANCEL_DEPOSIT_FORFEIT',
            reference_id: id,
            exchange_rate: 1,
            lines: [
              { account_id: ACCOUNTS.CLIENT_DEPOSITS, debit_amount: forfeit, credit_amount: 0, description: 'Deposit forfeited', party_type: 'client', party_id: clientId },
              { account_id: ACCOUNTS.FORFEITED_CLIENT_DEPOSITS, debit_amount: 0, credit_amount: forfeit, description: 'Forfeiture income' },
            ],
            created_by: username,
          });
        }
      }

      // 5. Flag the package itself cancelled
      await supabase.from('packages').update({
        cancelled: true, is_active: false, cancelled_by: username, cancelled_at: new Date().toISOString(), cancel_reason: cancelReason,
      }).eq('id', id);

      setCancelReview(null);
      loadData();
    } catch (e) {
      alert('Error cancelling package: ' + (e.message || e));
    } finally {
      setCancelSaving(false);
    }
  };

  const totalDepositLKR = packageSuppliers.reduce((s, ps) => {
    const r = ps.package_supplier_rates?.find(x => x.pax_type === 'Adult') || ps.package_supplier_rates?.[0];
    if (!r || r.is_included) return s;
    return s + parseFloat(r.non_refundable_deposit || 0) * parseFloat(r.budgeted_fx_rate || 1);
  }, 0);

  if (!pkg) return <div className="loading-screen"><div className="spinner" /></div>;

  const isAirline = (ps) => ps.suppliers?.supplier_type === 'Airline' || ps.service_type?.toLowerCase().includes('flight') || ps.service_type?.toLowerCase().includes('air');
  const numInput = (value, onChange, placeholder = '0') => (
    <input
      className="form-input"
      type="text"
      inputMode="decimal"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ MozAppearance: 'textfield' }}
    />
  );

  return (
    <div>
      {PasscodeModal}
      <div className="page-header">
        <div>
          <div className="page-title">{pkg.package_name}</div>
          <div className="page-subtitle">{pkg.package_code} &mdash; {pkg.country}{pkg.destination ? ', ' + pkg.destination : ''} {pkg.num_days ? '| ' + pkg.num_days + ' Days' : ''} {pkg.departure_date ? '| Dep: ' + pkg.departure_date : ''}</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-sm" style={{ background: 'rgba(239,68,68,0.1)', color: 'var(--danger)', border: 'none' }} onClick={async () => {
            // Require admin passcode
            try { await promptPasscode(); } catch { return; }
            // Block deletion if active bookings exist — they have posted journal entries referencing this package
            const { count } = await supabase.from('bookings').select('*', { count: 'exact', head: true })
              .eq('package_id', id).neq('booking_status', 'CANCELLED');
            if (count > 0) {
              alert(`Cannot delete — this package has ${count} active booking(s) with posted journal entries. Cancel all bookings first.`);
              return;
            }
            if (!window.confirm(`Delete package "${pkg.package_name}"? This cannot be undone.`)) return;
            // Reverse all commitment journals posted against this package
            const { data: journals } = await supabase.from('journal_entries')
              .select('id, is_reversed').eq('reference_type', 'package').eq('reference_id', id);
            for (const je of (journals || [])) {
              if (!je.is_reversed) await voidJournal(je.id, `Package ${pkg.package_code} deleted`, username);
            }
            // Nullify package_id on cancelled bookings so FK doesn't block deletion
            await supabase.from('bookings').update({ package_id: null }).eq('package_id', id).eq('booking_status', 'CANCELLED');
            await supabase.from('package_commission_tiers').delete().eq('package_id', id);
            await supabase.from('package_selling_prices').delete().eq('package_id', id);
            const { data: ps } = await supabase.from('package_suppliers').select('id').eq('package_id', id);
            if (ps?.length) {
              await supabase.from('package_supplier_rates').delete().in('package_supplier_id', ps.map(p => p.id));
              await supabase.from('package_suppliers').delete().eq('package_id', id);
            }
            const { error: delErr } = await supabase.from('packages').delete().eq('id', id);
            if (delErr) { alert('Delete failed: ' + delErr.message); return; }
            navigate('/packages');
          }}>Delete</button>
          <Link to={`/packages/${id}/edit`} className="btn btn-secondary">Edit Package</Link>
          {!pkg.confirmed && (
            <button className="btn btn-primary" style={{ background: '#d97706' }} onClick={confirmPackage}>
              Confirm Package
            </button>
          )}
          {pkg.confirmed && !pkg.cancelled && (
            <span style={{ fontSize: 12, fontWeight: 700, color: '#16a34a', background: '#f0fdf4', padding: '4px 12px', borderRadius: 20 }}>
              ✓ Confirmed
            </span>
          )}
          {pkg.confirmed && !pkg.cancelled && (
            <button className="btn btn-sm" style={{ background: 'rgba(239,68,68,0.1)', color: 'var(--danger)', border: 'none' }} onClick={openCancelReview}>
              Cancel Package
            </button>
          )}
          {pkg.cancelled && (
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--danger)', background: 'rgba(239,68,68,0.08)', padding: '4px 12px', borderRadius: 20 }}>
              Cancelled
            </span>
          )}
          {!pkg.is_active && !pkg.cancelled && (
            <button className="btn btn-primary" style={{ background: 'var(--success, #16a34a)' }} onClick={async () => {
              await supabase.from('packages').update({ is_active: true }).eq('id', id);
              loadData();
            }}>Publish Package</button>
          )}
          {pkg.is_active && (
            <Link to={`/bookings/new?package=${id}`} className="btn btn-primary">+ Book This Package</Link>
          )}
        </div>
      </div>

      {error && (
        <div style={{ color: 'var(--danger)', marginBottom: 14, padding: '10px 14px', background: 'rgba(239,68,68,0.06)', borderRadius: 7 }}>
          {error} <button style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)' }} onClick={() => setError('')}>×</button>
        </div>
      )}

      {(() => {
        // Estimated revenue: the pax count this package was built for (min_pax) ×
        // whichever selling-price tier actually covers that pax count.
        const builtForPax = pkg.min_pax || null;
        const adultPrices = sellingPrices.filter(sp => sp.pax_type === 'Adult');
        const matchedPrice = builtForPax
          ? (adultPrices.find(sp => builtForPax >= sp.pax_from && builtForPax <= sp.pax_to)
              || (adultPrices.length ? adultPrices.reduce((min, sp) => parseFloat(sp.sell_price_per_pax) < parseFloat(min.sell_price_per_pax) ? sp : min, adultPrices[0]) : null))
          : null;
        const estRevenue = builtForPax && matchedPrice ? builtForPax * parseFloat(matchedPrice.sell_price_per_pax) : null;
        return (
          <div className="stats-grid" style={{ marginBottom: 20 }}>
            {[
              ['Pax (Built For)', builtForPax || '—'],
              ['Est. Revenue', estRevenue ? `${matchedPrice.currencies?.code || ''} ${estRevenue.toLocaleString()}` : '—'],
              ['Suppliers', packageSuppliers.length],
              ['Invoices Raised', packageInvoices.filter(i => i.status !== 'VOID').length],
              ['Price Tiers', sellingPrices.length],
              ['Commission', pkg.commission_percent ? pkg.commission_percent + '% ' + (pkg.commission_basis || '') : pkg.commission_type === 'TIERED' ? 'Tiered' : 'Not set'],
              ['Status', pkg.is_active ? 'Active' : 'Inactive'],
            ].map(([l, v]) => (
              <div className="stat-card" key={l}><div className="stat-label">{l}</div><div className="stat-value">{v}</div></div>
            ))}
          </div>
        );
      })()}

      <div className="tabs" style={{ marginBottom: 16 }}>
        {[
          ['suppliers', `Suppliers & Rates (${packageSuppliers.length})`],
          ['prices', `Selling Prices (${sellingPrices.length})`],
          ['costing', 'Costing Preview'],
          ['commission', 'Commission'],
          ['invoices', `Invoices (${packageInvoices.filter(i => i.status !== 'VOID').length})`],
          ...(canSeePnl ? [['pnl', 'P&L']] : []),
        ].map(([t, l]) => (
          <div key={t} className={`tab ${tab === t ? 'active' : ''}`} onClick={() => { setTab(t); if (t === 'pnl' && !pnlData) loadPnl(); }}>{l}</div>
        ))}
      </div>

      {/* SUPPLIERS TAB */}
      {tab === 'suppliers' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {!unlocked && (
            <div className="card" style={{ background: 'var(--bg)', border: '1px dashed var(--border)', textAlign: 'center', padding: '14px 20px' }}>
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>🔒 Costs are locked for viewing. </span>
              <button className="btn btn-secondary btn-sm" onClick={unlockCosts}>Unlock to Edit</button>
            </div>
          )}
          {unlocked && (
          <div className="card">
            <div className="card-title" style={{ marginBottom: 4 }}>Add Supplier & Rate</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>Pick the supplier, enter what you're paying them, and the FX rate.</div>

            <div style={{ display: 'flex', gap: 16, marginBottom: 14 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                <input type="radio" checked={(sForm.cost_type || 'supplier') === 'supplier'}
                  onChange={() => setSForm(f => ({ ...f, cost_type: 'supplier', description: '' }))} /> Supplier
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                <input type="radio" checked={sForm.cost_type === 'internal'}
                  onChange={() => setSForm(f => ({ ...f, cost_type: 'internal', supplier_id: '', supplier_name: '' }))} /> Internal / Tour Operator Cost
              </label>
            </div>

            <div className="form-grid">
              {sForm.cost_type === 'internal' ? (
                <div className="form-group">
                  <label className="form-label">Description *</label>
                  <input className="form-input" value={sForm.description} onChange={e => setSForm(f => ({ ...f, description: e.target.value }))} placeholder="e.g. Guide allowance — Kasun" />
                </div>
              ) : (
                <div className="form-group">
                  <label className="form-label">Supplier *</label>
                  <SupplierPicker row={{ supplier_id: sForm.supplier_id, supplier_label: sForm.supplier_name }}
                    onChange={p => setSForm(f => ({ ...f, supplier_id: p.supplier_id ?? f.supplier_id, supplier_name: p.supplier_label ?? f.supplier_name }))} />
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Service Type</label>
                <TypeSearch
                  options={SERVICE_TYPES}
                  value={sForm.service_type}
                  onSelect={s => setSForm(f => ({ ...f, service_type: s.label }))}
                  onClear={() => setSForm(f => ({ ...f, service_type: '' }))}
                  placeholder="Hotel / Flight / Tour..."
                  displayKey="label"
                />
              </div>
              {sForm.cost_type !== 'internal' && (
              <div className="form-group">
                <label className="form-label">Description</label>
                <input className="form-input" value={sForm.description} onChange={e => setSForm(f => ({ ...f, description: e.target.value }))} placeholder="e.g. 5 nights Burj Al Arab" />
              </div>
              )}
            </div>

            <div style={{ borderTop: '1px solid var(--border)', margin: '14px 0 14px', paddingTop: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Rate / Costing</div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Pax Type</label>
                  <select className="form-input" value={sForm.pax_type} onChange={e => setSForm(f => ({ ...f, pax_type: e.target.value }))}>
                    {COST_PAX_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  {unlocked ? (
                    <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                      <input className="form-input" style={{ fontSize: 12, padding: '4px 8px' }} placeholder="+ New pax type name..."
                        value={newPaxTypeName} onChange={e => setNewPaxTypeName(e.target.value)} />
                      <button className="btn btn-secondary btn-sm" disabled={!newPaxTypeName.trim() || addingPaxType}
                        onClick={() => addPaxType(false)} title="A real passenger category, selectable on invoice line items and selling prices too">Add</button>
                      <button className="btn btn-secondary btn-sm" disabled={!newPaxTypeName.trim() || addingPaxType}
                        onClick={() => addPaxType(true)} title="A shared cost only (like Common), never a real passenger type — not offered on line items">Add as Common</button>
                    </div>
                  ) : (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>🔒 Unlock to add or remove pax types</div>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                    {paxTypes.map(t => (
                      <span key={t.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, padding: '2px 6px', borderRadius: 4, background: 'var(--bg)', border: '1px solid var(--border)' }}>
                        {t.name}{t.is_common ? ' (common)' : ''}
                        {unlocked && (
                          <button onClick={() => removePaxType(t.id)} title="Remove this pax type (existing rates keep it, just won't offer it for new ones)"
                            style={{ border: 'none', background: 'none', color: 'var(--danger)', cursor: 'pointer', padding: 0, fontSize: 12, lineHeight: 1 }}>✕</button>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Cost Per Pax *</label>
                  {numInput(sForm.cost_per_pax, v => setSForm(f => ({ ...f, cost_per_pax: v })), 'e.g. 150')}
                </div>
                <div className="form-group">
                  <label className="form-label">Currency *</label>
                  <TypeSearch
                    options={currencies}
                    value={sForm.currency_code}
                    onSelect={c => setSForm(f => ({ ...f, currency_id: c.id, currency_code: c.code }))}
                    onClear={() => setSForm(f => ({ ...f, currency_id: '', currency_code: '' }))}
                    placeholder="USD / LKR..."
                    displayKey="code"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Budgeted FX Rate → LKR</label>
                  {numInput(sForm.budgeted_fx_rate, v => setSForm(f => ({ ...f, budgeted_fx_rate: v })), 'e.g. 320 for USD')}
                </div>
                {sForm.cost_per_pax && sForm.budgeted_fx_rate && (
                  <div className="form-group">
                    <label className="form-label">= LKR per pax</label>
                    <div style={{ padding: '10px 12px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 7, fontWeight: 700, fontSize: 15 }}>
                      {(parseFloat(sForm.cost_per_pax || 0) * parseFloat(sForm.budgeted_fx_rate || 1)).toLocaleString()}
                    </div>
                  </div>
                )}
              </div>

              {(allSuppliers.find(s => s.id === sForm.supplier_id)?.supplier_type === 'Airline' || sForm.service_type === 'Flight') && (
                <div className="form-grid" style={{ marginTop: 8 }}>
                  <div className="form-group">
                    <label className="form-label">Non-Refundable Deposit (Airline)</label>
                    {numInput(sForm.non_refundable_deposit, v => setSForm(f => ({ ...f, non_refundable_deposit: v })), '0.00')}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Deposit Currency</label>
                    <TypeSearch
                      options={currencies}
                      value={sForm.deposit_currency_code}
                      onSelect={c => setSForm(f => ({ ...f, deposit_currency_id: c.id, deposit_currency_code: c.code }))}
                      onClear={() => setSForm(f => ({ ...f, deposit_currency_id: '', deposit_currency_code: '' }))}
                      placeholder="Same as above..."
                      displayKey="code"
                    />
                  </div>
                  {parseFloat(sForm.non_refundable_deposit) > 0 && (
                    <div className="form-group full-width" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      This deposit becomes a <strong>payable to {allSuppliers.find(s => s.id === sForm.supplier_id)?.name || 'the supplier'}</strong> — no bank entry is made.
                      The bank moves only when it is actually paid, through a Payment Voucher.
                    </div>
                  )}
                </div>
              )}

              <div className="form-grid" style={{ marginTop: 8 }}>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <input className="form-input" value={sForm.notes} onChange={e => setSForm(f => ({ ...f, notes: e.target.value }))} placeholder="e.g. twin sharing, includes breakfast" />
                </div>
                <div className="form-group" style={{ justifyContent: 'flex-end', flexDirection: 'column', gap: 10 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, marginTop: 24 }}>
                    <input type="checkbox" checked={sForm.is_included} onChange={e => setSForm(f => ({ ...f, is_included: e.target.checked }))} />
                    Included in another rate
                  </label>
                  <button className="btn btn-primary" onClick={addSupplier} disabled={saving === 'supplier' || (sForm.cost_type === 'internal' ? !sForm.description : !sForm.supplier_id) || !sForm.cost_per_pax}>
                    {saving === 'supplier' ? 'Adding...' : 'Add Supplier & Rate'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          )}

          {[...packageSuppliers].sort((a, b) => {
            // Internal costs last; suppliers alphabetically so the same supplier is always in the same place
            const an = a.cost_type === 'internal' ? '\uffff' + (a.description || '') : (a.suppliers?.name || '');
            const bn = b.cost_type === 'internal' ? '\uffff' + (b.description || '') : (b.suppliers?.name || '');
            return an.localeCompare(bn);
          }).map(ps => {
            const airline = isAirline(ps);
            const hasRates = ps.package_supplier_rates?.length > 0;
            const open = !hasRates || rForm.package_supplier_id === ps.id;
            return (
              <div className="card" key={ps.id}>
                <div className="card-header">
                  <div>
                    <div className="card-title">{ps.cost_type === 'internal' ? (ps.description || 'Internal cost') : ps.suppliers?.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                      {ps.service_type}{ps.cost_type !== 'internal' && ps.description ? ' — ' + ps.description : ''}
                      {airline && <span className="badge badge-blue" style={{ marginLeft: 8 }}>Airline</span>}
                      {ps.cost_type === 'internal' && <span className="badge badge-yellow" style={{ marginLeft: 8 }}>Internal / Tour Operator Cost</span>}
                    </div>
                  </div>
                  {unlocked && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    {hasRates && (
                      <button className="btn btn-secondary btn-sm" onClick={() => setRForm(f => ({ ...f, package_supplier_id: open ? '' : ps.id, supplier_type: ps.suppliers?.supplier_type || '' }))}>
                        {open ? 'Cancel' : '+ Add Rate'}
                      </button>
                    )}
                    <button className="btn btn-sm" style={{ background: 'rgba(239,68,68,0.1)', color: 'var(--danger)', border: 'none' }} onClick={() => deleteSupplier(ps.id)} disabled={deletingId === ps.id}>
                      {deletingId === ps.id ? '...' : 'Delete'}
                    </button>
                  </div>
                  )}
                </div>

                {unlocked && open && (
                  <div style={{ background: 'var(--bg)', borderRadius: 8, padding: 16, marginBottom: 14 }}>
                    {!hasRates && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>Enter the rate for this supplier to complete the costing.</div>}
                    <div className="form-grid">
                      <div className="form-group">
                        <label className="form-label">Pax Type</label>
                        <select className="form-input"
                          value={rForm.package_supplier_id === ps.id ? rForm.pax_type : 'Adult'}
                          onChange={e => setRForm(f => ({ ...f, pax_type: e.target.value, package_supplier_id: ps.id }))}>
                          {COST_PAX_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">{airline ? 'Ticket Cost Per Pax *' : 'Cost Per Pax *'}</label>
                        {numInput(
                          rForm.package_supplier_id === ps.id ? rForm.cost_per_pax : '',
                          v => setRForm(f => ({ ...f, cost_per_pax: v, package_supplier_id: ps.id })),
                          '0.00'
                        )}
                      </div>
                      <div className="form-group">
                        <label className="form-label">Currency *</label>
                        <TypeSearch
                          options={currencies}
                          value={rForm.package_supplier_id === ps.id ? rForm.currency_code : ''}
                          onSelect={c => setRForm(f => ({ ...f, currency_id: c.id, currency_code: c.code, package_supplier_id: ps.id }))}
                          onClear={() => setRForm(f => ({ ...f, currency_id: '', currency_code: '' }))}
                          placeholder="USD / LKR..."
                          displayKey="code"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Budgeted FX Rate (to LKR)</label>
                        {numInput(
                          rForm.package_supplier_id === ps.id ? rForm.budgeted_fx_rate : '1',
                          v => setRForm(f => ({ ...f, budgeted_fx_rate: v, package_supplier_id: ps.id })),
                          'e.g. 320 for USD'
                        )}
                      </div>

                      {airline && (
                        <>
                          <div className="form-group">
                            <label className="form-label">Non-Refundable Deposit</label>
                            {numInput(rForm.non_refundable_deposit, v => setRForm(f => ({ ...f, non_refundable_deposit: v })), '0.00')}
                          </div>
                          <div className="form-group">
                            <label className="form-label">Deposit Currency</label>
                            <TypeSearch
                              options={currencies}
                              value={rForm.deposit_currency_code}
                              onSelect={c => setRForm(f => ({ ...f, deposit_currency_id: c.id, deposit_currency_code: c.code }))}
                              onClear={() => setRForm(f => ({ ...f, deposit_currency_id: '', deposit_currency_code: '' }))}
                              placeholder="Same as above..."
                              displayKey="code"
                            />
                          </div>
                        </>
                      )}

                      <div className="form-group">
                        <label className="form-label">Notes</label>
                        <input className="form-input" value={rForm.notes} onChange={e => setRForm(f => ({ ...f, notes: e.target.value }))} placeholder="e.g. includes baggage, twin sharing" />
                      </div>
                      <div className="form-group" style={{ justifyContent: 'flex-end', flexDirection: 'column', gap: 10 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, marginTop: 24 }}>
                          <input type="checkbox" checked={rForm.is_included} onChange={e => setRForm(f => ({ ...f, is_included: e.target.checked }))} />
                          Included in another rate
                        </label>
                        <button className="btn btn-primary" onClick={addRate} disabled={saving === 'rate'}>{saving === 'rate' ? 'Saving...' : 'Save Rate'}</button>
                      </div>
                    </div>
                    {rForm.cost_per_pax && rForm.budgeted_fx_rate && (
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
                        Cost in LKR: <strong>{(parseFloat(rForm.cost_per_pax) * parseFloat(rForm.budgeted_fx_rate)).toLocaleString()}</strong> / pax
                        {rForm.non_refundable_deposit && <> &nbsp;| Deposit in LKR: <strong>{(parseFloat(rForm.non_refundable_deposit) * parseFloat(rForm.budgeted_fx_rate)).toLocaleString()}</strong></>}
                      </div>
                    )}
                  </div>
                )}

                <div className="table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th>Pax Type</th>
                        <th>{airline ? 'Ticket Cost/Pax' : 'Cost/Pax'}</th>
                        <th>Currency</th>
                        <th>Budgeted FX</th>
                        <th style={{ textAlign: 'right' }}>Cost in LKR</th>
                        {airline && <th style={{ textAlign: 'right' }}>Non-Refund Deposit</th>}
                        <th>Notes</th>
                        <th>Included</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...(ps.package_supplier_rates || [])].sort((a, b) => paxTypeRank(a.pax_type) - paxTypeRank(b.pax_type)).map(r => (
                        editRate?.id === r.id ? (
                          <tr key={r.id} style={{ background: 'var(--bg)' }}>
                            <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.pax_type || 'Adult'}</td>
                            <td>
                              <input className="form-input" style={{ padding: '4px 8px', width: 100 }} type="text" inputMode="decimal"
                                value={editRate.cost_per_pax} onChange={e => setEditRate(f => ({ ...f, cost_per_pax: e.target.value }))} />
                            </td>
                            <td>
                              <TypeSearch
                                options={currencies}
                                value={editRate.currency_code || currencies.find(c => c.id === editRate.currency_id)?.code || ''}
                                onSelect={c => setEditRate(f => ({ ...f, currency_id: c.id, currency_code: c.code }))}
                                onClear={() => setEditRate(f => ({ ...f, currency_id: '', currency_code: '' }))}
                                placeholder="Currency"
                                displayKey="code"
                              />
                            </td>
                            <td>
                              <input className="form-input" style={{ padding: '4px 8px', width: 80 }} type="text" inputMode="decimal"
                                value={editRate.budgeted_fx_rate} onChange={e => setEditRate(f => ({ ...f, budgeted_fx_rate: e.target.value }))} />
                            </td>
                            <td style={{ textAlign: 'right', fontWeight: 600 }} className="amount">
                              {(parseFloat(editRate.cost_per_pax || 0) * parseFloat(editRate.budgeted_fx_rate || 1)).toLocaleString()}
                            </td>
                            {airline && <td></td>}
                            <td><input className="form-input" style={{ padding: '4px 8px' }} value={editRate.notes || ''} onChange={e => setEditRate(f => ({ ...f, notes: e.target.value }))} placeholder="Notes" /></td>
                            <td>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12 }}>
                                <input type="checkbox" checked={!!editRate.is_included} onChange={e => setEditRate(f => ({ ...f, is_included: e.target.checked }))} />
                                Included
                              </label>
                            </td>
                            <td>
                              <div style={{ display: 'flex', gap: 4 }}>
                                <button className="btn btn-primary btn-sm" onClick={saveEditRate} disabled={saving === 'editRate'}>Save</button>
                                <button className="btn btn-secondary btn-sm" onClick={() => setEditRate(null)}>✕</button>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          <tr key={r.id} style={{ opacity: r.is_included ? 0.7 : 1 }}>
                            <td style={{ fontSize: 12 }}>{r.pax_type || 'Adult'}</td>
                            <td className="amount" style={{ fontWeight: 600 }}>{parseFloat(r.cost_per_pax).toLocaleString()}</td>
                            <td><span className="badge badge-blue">{r.currency?.code}</span></td>
                            <td>{r.budgeted_fx_rate || 1}</td>
                            <td style={{ textAlign: 'right' }} className="amount">
                              {r.is_included
                                ? <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Included</span>
                                : (parseFloat(r.cost_per_pax) * parseFloat(r.budgeted_fx_rate || 1)).toLocaleString()
                              }
                            </td>
                            {airline && (
                              <td style={{ textAlign: 'right' }} className="amount">
                                {parseFloat(r.non_refundable_deposit || 0) > 0
                                  ? <span style={{ color: 'var(--danger)' }}>{r.deposit_currency?.code || r.currency?.code} {parseFloat(r.non_refundable_deposit).toLocaleString()}</span>
                                  : <span style={{ color: 'var(--text-muted)' }}>None</span>}
                              </td>
                            )}
                            <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{r.notes || ''}</td>
                            <td>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: unlocked ? 'pointer' : 'default', fontSize: 12 }}>
                                <input type="checkbox" checked={!!r.is_included} disabled={!unlocked} onChange={() => toggleIncluded(r.id, r.is_included)} />
                                {r.is_included && <span style={{ color: 'var(--text-muted)' }}>Included</span>}
                              </label>
                            </td>
                            <td>
                              {unlocked && (
                              <div style={{ display: 'flex', gap: 4 }}>
                                <button className="btn btn-secondary btn-sm" onClick={() => setEditRate({ id: r.id, cost_per_pax: r.cost_per_pax, currency_id: r.currency_id, currency_code: r.currency?.code || '', budgeted_fx_rate: r.budgeted_fx_rate || 1, notes: r.notes || '', is_included: !!r.is_included })}>Edit</button>
                                <button className="btn btn-sm" style={{ background: 'rgba(239,68,68,0.1)', color: 'var(--danger)', border: 'none' }} onClick={() => deleteRate(r.id)} disabled={deletingId === r.id}>{deletingId === r.id ? '...' : '✕'}</button>
                              </div>
                              )}
                            </td>
                          </tr>
                        )
                      ))}
                      {!ps.package_supplier_rates?.length && (
                        <tr><td colSpan={airline ? 8 : 7} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 12 }}>No rates yet</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
          {!packageSuppliers.length && <div className="empty-state"><div className="empty-state-title">No suppliers added yet</div></div>}
        </div>
      )}

      {/* SELLING PRICES TAB */}
      {tab === 'prices' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {!unlocked && (
            <div className="card" style={{ background: 'var(--bg)', border: '1px dashed var(--border)', textAlign: 'center', padding: '14px 20px' }}>
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>🔒 Selling prices are locked for viewing. </span>
              <button className="btn btn-secondary btn-sm" onClick={unlockCosts}>Unlock to Edit</button>
            </div>
          )}
          {unlocked && (
          <div className="card">
            <div className="card-title" style={{ marginBottom: 14 }}>Add Selling Price Tier</div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Pax Type</label>
                <select className="form-input" value={pForm.pax_type} onChange={e => setPForm(f => ({ ...f, pax_type: e.target.value }))}>
                  {PAX_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Pax From</label>
                {numInput(pForm.pax_from, v => setPForm(f => ({ ...f, pax_from: v })))}
              </div>
              <div className="form-group">
                <label className="form-label">Pax To</label>
                {numInput(pForm.pax_to, v => setPForm(f => ({ ...f, pax_to: v })))}
              </div>
              <div className="form-group">
                <label className="form-label">Sell Price Per Pax *</label>
                {numInput(pForm.sell_price_per_pax, v => setPForm(f => ({ ...f, sell_price_per_pax: v })), '0.00')}
              </div>
              <div className="form-group">
                <label className="form-label">Currency *</label>
                <TypeSearch
                  options={currencies}
                  value={pForm.currency_code}
                  onSelect={c => setPForm(f => ({ ...f, currency_id: c.id, currency_code: c.code }))}
                  onClear={() => setPForm(f => ({ ...f, currency_id: '', currency_code: '' }))}
                  placeholder="USD / LKR..."
                  displayKey="code"
                />
              </div>
              <div className="form-group" style={{ justifyContent: 'flex-end' }}>
                <label className="form-label">&nbsp;</label>
                <button className="btn btn-primary" onClick={addSellingPrice} disabled={saving === 'price'}>{saving === 'price' ? 'Saving...' : 'Add Tier'}</button>
              </div>
            </div>
          </div>
          )}
          <div className="card">
            <div className="table-wrapper">
              <table>
                <thead><tr><th>Pax Type</th><th>Pax Range</th><th style={{ textAlign: 'right' }}>Sell / Pax</th><th>Currency</th><th></th></tr></thead>
                <tbody>
                  {sellingPrices.map(p => (
                    <tr key={p.id}>
                      <td>{p.pax_type || 'Adult'}</td>
                      <td>{p.pax_from} — {p.pax_to} pax</td>
                      <td style={{ textAlign: 'right' }} className="amount">{parseFloat(p.sell_price_per_pax).toLocaleString()}</td>
                      <td><span className="badge badge-green">{p.currencies?.code}</span></td>
                      <td>
                        {unlocked && (
                          <button className="btn btn-sm" style={{ background: 'rgba(239,68,68,0.1)', color: 'var(--danger)', border: 'none' }} onClick={() => deleteSellingPrice(p.id)} disabled={deletingId === p.id}>{deletingId === p.id ? '...' : '✕'}</button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!sellingPrices.length && <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 20 }}>No price tiers yet</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* COSTING PREVIEW TAB */}
      {tab === 'costing' && (
        <div className="card">
          <div className="card-title" style={{ marginBottom: 16 }}>Package Costing — Per Pax at Budgeted FX</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                <th style={{ textAlign: 'left', padding: '8px 0' }}>Supplier</th>
                <th style={{ textAlign: 'left' }}>Service</th>
                <th style={{ textAlign: 'right' }}>Cost/Pax (FC)</th>
                <th style={{ textAlign: 'right' }}>FX Rate</th>
                <th style={{ textAlign: 'right' }}>Cost (LKR)</th>
                <th style={{ textAlign: 'right' }}>NR Deposit (LKR)</th>
              </tr>
            </thead>
            <tbody>
              {packageSuppliers.map(ps => {
                const r = ps.package_supplier_rates?.find(x => x.pax_type === 'Adult') || ps.package_supplier_rates?.[0];
                const costLKR = r ? parseFloat(r.cost_per_pax || 0) * parseFloat(r.budgeted_fx_rate || 1) : 0;
                const depositLKR = r ? parseFloat(r.non_refundable_deposit || 0) * parseFloat(r.budgeted_fx_rate || 1) : 0;
                return (
                  <tr key={ps.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 0', fontWeight: 500 }}>{ps.cost_type === 'internal' ? (ps.description || 'Internal cost') : ps.suppliers?.name}</td>
                    <td style={{ color: 'var(--text-muted)' }}>{ps.service_type}</td>
                    <td style={{ textAlign: 'right' }} className="amount">{r ? parseFloat(r.cost_per_pax).toLocaleString() + ' ' + (r.currency?.code || '') : '—'}</td>
                    <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>{r?.budgeted_fx_rate || '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }} className="amount">
                      {r?.is_included
                        ? <span style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 400 }}>Included</span>
                        : r ? costLKR.toLocaleString() : '—'}
                    </td>
                    <td style={{ textAlign: 'right', color: depositLKR > 0 ? 'var(--danger)' : 'var(--text-muted)' }} className="amount">
                      {depositLKR > 0 ? depositLKR.toLocaleString() : '—'}
                    </td>
                  </tr>
                );
              })}
              <tr style={{ borderTop: '2px solid var(--border)', fontWeight: 700, background: 'var(--bg)' }}>
                <td colSpan={4} style={{ padding: '8px 0', textAlign: 'right', color: 'var(--text-muted)' }}>Total Per Pax (LKR)</td>
                <td style={{ textAlign: 'right' }} className="amount">{totalCostPerPaxLKR.toLocaleString()}</td>
                <td style={{ textAlign: 'right', color: 'var(--danger)' }} className="amount">{totalDepositLKR > 0 ? totalDepositLKR.toLocaleString() : '—'}</td>
              </tr>
            </tbody>
          </table>

          {sellingPrices.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12, color: 'var(--text-muted)' }}>GP by Selling Price Tier</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border)' }}>
                    <th style={{ textAlign: 'left', padding: '8px 0' }}>Pax Range</th>
                    <th style={{ textAlign: 'right' }}>Sell/Pax</th>
                    <th style={{ textAlign: 'right' }}>Total Cost (LKR)</th>
                    <th style={{ textAlign: 'right', color: 'var(--success)' }}>GP (LKR)</th>
                    <th style={{ textAlign: 'right', color: 'var(--success)' }}>GP %</th>
                  </tr>
                </thead>
                <tbody>
                  {sellingPrices.map(sp => {
                    const sell = parseFloat(sp.sell_price_per_pax || 0);
                    const gp = sell - totalCostPerPaxLKR;
                    const gpPct = sell > 0 ? ((gp / sell) * 100).toFixed(1) : 0;
                    return (
                      <tr key={sp.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '8px 0' }}>{sp.pax_from} — {sp.pax_to} pax</td>
                        <td style={{ textAlign: 'right' }} className="amount">{sell.toLocaleString()} {sp.currencies?.code}</td>
                        <td style={{ textAlign: 'right', color: 'var(--danger)' }} className="amount">{totalCostPerPaxLKR.toLocaleString()}</td>
                        <td style={{ textAlign: 'right', color: gp >= 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 600 }} className="amount">{gp.toLocaleString()}</td>
                        <td style={{ textAlign: 'right', color: parseFloat(gpPct) >= 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 600 }}>{gpPct}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* COMMISSION TAB */}
      {tab === 'commission' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {!unlocked && (
            <div className="card" style={{ background: 'var(--bg)', border: '1px dashed var(--border)', textAlign: 'center', padding: '14px 20px' }}>
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>🔒 Commission settings are locked for viewing. </span>
              <button className="btn btn-secondary btn-sm" onClick={unlockCosts}>Unlock to Edit</button>
            </div>
          )}
          {unlocked && (
          <div className="card">
            <div className="card-title" style={{ marginBottom: 6 }}>Commission Type</div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '8px 16px', borderRadius: 7, border: `2px solid ${commType === 'PERCENT' ? 'var(--primary)' : 'var(--border)'}`, background: commType === 'PERCENT' ? 'rgba(99,102,241,0.06)' : '' }}>
                <input type="radio" checked={commType === 'PERCENT'} onChange={() => setCommType('PERCENT')} /> Percentage of GP/Sell/Cost
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '8px 16px', borderRadius: 7, border: `2px solid ${commType === 'TIERED' ? 'var(--primary)' : 'var(--border)'}`, background: commType === 'TIERED' ? 'rgba(99,102,241,0.06)' : '' }}>
                <input type="radio" checked={commType === 'TIERED'} onChange={() => setCommType('TIERED')} /> Tiered Fixed Amount (LKR by GP)
              </label>
            </div>

            {commType === 'PERCENT' && (
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Commission %</label>
                  {numInput(commForm.commission_percent, v => setCommForm(f => ({ ...f, commission_percent: v })), 'e.g. 5')}
                </div>
                <div className="form-group">
                  <label className="form-label">Calculated On</label>
                  <TypeSearch
                    options={COMM_BASIS_OPTIONS}
                    value={commForm.commission_basis_label}
                    onSelect={o => setCommForm(f => ({ ...f, commission_basis: o.id, commission_basis_label: o.label }))}
                    onClear={() => setCommForm(f => ({ ...f, commission_basis: 'ON_GP', commission_basis_label: 'Gross Profit (GP)' }))}
                    placeholder="Gross Profit / Selling Price..."
                    displayKey="label"
                  />
                </div>
                <div className="form-group" style={{ justifyContent: 'flex-end' }}>
                  <label className="form-label">&nbsp;</label>
                  <button className="btn btn-primary" onClick={saveCommission} disabled={saving === 'comm'}>{saving === 'comm' ? 'Saving...' : 'Save Rule'}</button>
                </div>
              </div>
            )}

            {commType === 'TIERED' && (
              <div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14 }}>
                  Define commission tiers based on GP per pax (LKR). The agent earns a fixed amount when GP falls in that range.
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">GP From (LKR)</label>
                    {numInput(tierForm.gp_from, v => setTierForm(f => ({ ...f, gp_from: v })), 'e.g. 15000')}
                  </div>
                  <div className="form-group">
                    <label className="form-label">GP To (LKR) — blank = unlimited</label>
                    {numInput(tierForm.gp_to, v => setTierForm(f => ({ ...f, gp_to: v })), 'blank = and above')}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Agent Commission (LKR)</label>
                    {numInput(tierForm.commission_amount, v => setTierForm(f => ({ ...f, commission_amount: v })), 'e.g. 3000')}
                  </div>
                  <div className="form-group" style={{ justifyContent: 'flex-end' }}>
                    <label className="form-label">&nbsp;</label>
                    <button className="btn btn-primary" onClick={addCommTier} disabled={saving === 'tier'}>{saving === 'tier' ? 'Adding...' : 'Add Tier'}</button>
                  </div>
                </div>
                <div className="table-wrapper" style={{ marginTop: 16 }}>
                  <table>
                    <thead><tr><th>GP From</th><th>GP To</th><th style={{ textAlign: 'right' }}>Agent Commission (LKR)</th><th></th></tr></thead>
                    <tbody>
                      {commTiers.map(t => (
                        <tr key={t.id}>
                          <td className="amount">≥ {parseFloat(t.gp_from).toLocaleString()}</td>
                          <td className="amount">{t.gp_to != null ? '< ' + parseFloat(t.gp_to).toLocaleString() : 'and above'}</td>
                          <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--primary)' }} className="amount">{parseFloat(t.commission_amount).toLocaleString()}</td>
                          <td><button className="btn btn-sm" style={{ background: 'rgba(239,68,68,0.1)', color: 'var(--danger)', border: 'none' }} onClick={() => deleteCommTier(t.id)}>✕</button></td>
                        </tr>
                      ))}
                      {!commTiers.length && <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 16 }}>No tiers yet</td></tr>}
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: 12 }}>
                  <button className="btn btn-primary" onClick={saveCommission} disabled={saving === 'comm'}>{saving === 'comm' ? 'Saving...' : 'Save Commission Type'}</button>
                </div>
              </div>
            )}
          </div>
          )}

          {commType === 'PERCENT' && pkg.commission_percent > 0 && sellingPrices.length > 0 && (
            <div className="card">
              <div className="card-title" style={{ marginBottom: 12 }}>Estimated Commission by Selling Tier</div>
              {sellingPrices.map(sp => {
                const sell = parseFloat(sp.sell_price_per_pax || 0);
                const gp = sell - totalCostPerPaxLKR;
                const base = pkg.commission_basis === 'ON_GP' ? gp : pkg.commission_basis === 'ON_SELL' ? sell : totalCostPerPaxLKR;
                const comm = base * (parseFloat(pkg.commission_percent) / 100);
                return (
                  <div key={sp.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                    <span>{sp.pax_from}–{sp.pax_to} pax @ {sp.currencies?.code} {parseFloat(sp.sell_price_per_pax).toLocaleString()}</span>
                    <span style={{ fontWeight: 600, color: 'var(--primary)' }}>LKR {comm.toLocaleString(undefined, { maximumFractionDigits: 0 })} / pax</span>
                  </div>
                );
              })}
            </div>
          )}

          {commType === 'TIERED' && commTiers.length > 0 && sellingPrices.length > 0 && (
            <div className="card">
              <div className="card-title" style={{ marginBottom: 12 }}>Commission Preview by Selling Tier</div>
              {sellingPrices.map(sp => {
                const sell = parseFloat(sp.sell_price_per_pax || 0);
                const gp = sell - totalCostPerPaxLKR;
                const matchedTier = commTiers.slice().sort((a, b) => parseFloat(b.gp_from) - parseFloat(a.gp_from))
                  .find(t => gp >= parseFloat(t.gp_from) && (t.gp_to == null || gp < parseFloat(t.gp_to)));
                const comm = matchedTier ? parseFloat(matchedTier.commission_amount) : 0;
                return (
                  <div key={sp.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                    <div>
                      <span>{sp.pax_from}–{sp.pax_to} pax @ {sp.currencies?.code} {parseFloat(sp.sell_price_per_pax).toLocaleString()}</span>
                      <span style={{ color: 'var(--text-muted)', marginLeft: 10 }}>GP: LKR {gp.toLocaleString()}</span>
                    </div>
                    <span style={{ fontWeight: 600, color: comm > 0 ? 'var(--primary)' : 'var(--text-muted)' }}>
                      {comm > 0 ? `LKR ${comm.toLocaleString()} / pax` : 'No commission'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* INVOICES TAB — every invoice raised for this package */}
      {tab === 'invoices' && (() => {
        const live = packageInvoices.filter(i => i.status !== 'VOID');
        const invTotal = (i) => (i.line_items || []).reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
        const totalSell = live.reduce((s, i) => s + invTotal(i), 0);
        const paxOf = (i) => (i.line_items || []).reduce((s, l) => s + (parseFloat(l.qty) || 1), 0);
        const totalPax = live.reduce((s, i) => s + paxOf(i), 0);
        return (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
              <div>
                <div className="card-title" style={{ marginBottom: 2 }}>Invoices Raised ({live.length})</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {totalPax} pax · LKR {totalSell.toLocaleString(undefined, { maximumFractionDigits: 0 })} invoiced{packageInvoices.length > live.length ? ` · ${packageInvoices.length - live.length} void shown greyed` : ''}
                </div>
              </div>
              {canSeePnl && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <input className="form-input" style={{ width: 170 }} placeholder="TLS-26-0000" value={linkInvoiceNo}
                    onChange={e => setLinkInvoiceNo(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') linkInvoice(); }} />
                  <button className="btn btn-secondary btn-sm" onClick={linkInvoice}>Link invoice</button>
                </div>
              )}
            </div>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Invoice</th><th>Date</th><th>Client</th><th>Pax</th>
                    <th style={{ textAlign: 'right' }}>Amount (LKR)</th><th>Status</th>{canSeePnl && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {packageInvoices.map(i => (
                    <tr key={i.id} style={{ opacity: i.status === 'VOID' ? 0.5 : 1 }}>
                      <td><Link to={`/invoices/${i.id}`} style={{ color: 'var(--primary)' }}>{i.invoice_number}</Link></td>
                      <td>{i.issued_date}</td>
                      <td>{i.clients?.full_name || '—'}{i.clients?.client_code ? <span style={{ fontSize: 11, color: 'var(--text-muted)' }}> · {i.clients.client_code}</span> : null}</td>
                      <td>{paxOf(i)}</td>
                      <td style={{ textAlign: 'right' }} className="amount">{invTotal(i).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                      <td>{i.status}</td>
                      {canSeePnl && (
                        <td>{i.package_id === id && (
                          <button className="btn btn-sm" style={{ fontSize: 11 }} onClick={() => unlinkInvoice(i)}>Unlink</button>
                        )}</td>
                      )}
                    </tr>
                  ))}
                  {!packageInvoices.length && <tr><td colSpan={canSeePnl ? 7 : 6} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 16 }}>No invoices raised for this package yet</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* P&L TAB — read-only reference view, actual figures from the ledger as of today */}
      {tab === 'pnl' && canSeePnl && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {pnlLoading ? (
            <div className="card" style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading...</div>
          ) : !pnlData ? null : (
            <>
              <div className="card">
                <div className="card-title" style={{ marginBottom: 4 }}>Actual (as of today)</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>From real invoices raised and real costs posted against this package's bookings so far.</div>
                <div className="stats-grid">
                  {[
                    ['Revenue', `LKR ${pnlData.actualRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`],
                    ['Cost', `LKR ${pnlData.actualCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}`],
                    ['Gross Profit', `LKR ${(pnlData.actualRevenue - pnlData.actualCost).toLocaleString(undefined, { maximumFractionDigits: 0 })}`],
                    ['GP %', pnlData.actualRevenue > 0 ? `${(((pnlData.actualRevenue - pnlData.actualCost) / pnlData.actualRevenue) * 100).toFixed(1)}%` : '—'],
                  ].map(([l, v]) => (
                    <div className="stat-card" key={l}><div className="stat-label">{l}</div><div className="stat-value">{v}</div></div>
                  ))}
                </div>
              </div>

              <div className="card">
                <div className="card-title" style={{ marginBottom: 4 }}>Budgeted (for the {pnlData.totalPax} pax booked so far)</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>What the rate card says this many pax should cost/sell for — for comparison against the actuals above, not the package's full planned capacity.</div>
                <div className="stats-grid">
                  {[
                    ['Revenue', pnlData.budgetedRevenue != null ? `${pnlData.budgetedCurrency || 'LKR'} ${pnlData.budgetedRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '—'],
                    ['Cost', `LKR ${pnlData.budgetedCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}`],
                    ['Gross Profit', pnlData.budgetedRevenue != null ? `${pnlData.budgetedCurrency || 'LKR'} ${(pnlData.budgetedRevenue - pnlData.budgetedCost).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '—'],
                    ['GP %', pnlData.budgetedRevenue > 0 ? `${(((pnlData.budgetedRevenue - pnlData.budgetedCost) / pnlData.budgetedRevenue) * 100).toFixed(1)}%` : '—'],
                  ].map(([l, v]) => (
                    <div className="stat-card" key={l}><div className="stat-label">{l}</div><div className="stat-value">{v}</div></div>
                  ))}
                </div>
              </div>

              <div className="card">
                <div className="card-title" style={{ marginBottom: 12 }}>By Booking</div>
                <div className="table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th>Client</th><th>Pax</th><th>Status</th><th>Invoice</th>
                        <th style={{ textAlign: 'right' }}>Revenue</th>
                        <th style={{ textAlign: 'right' }}>Cost</th>
                        <th style={{ textAlign: 'right' }}>GP</th>
                        <th style={{ textAlign: 'right' }}>GP %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pnlData.rows.map(r => {
                        const gp = r.revenue - r.cost;
                        return (
                          <tr key={r.booking_id} style={{ opacity: r.status === 'CANCELLED' ? 0.5 : 1 }}>
                            <td>{r.client_name}</td>
                            <td>{r.num_pax}</td>
                            <td>{r.status}</td>
                            <td>
                              {r.invoice_id
                                ? <Link to={`/invoices/${r.invoice_id}`} style={{ color: 'var(--primary)' }}>{r.invoice_number}</Link>
                                : <span style={{ color: 'var(--text-muted)' }}>Not invoiced</span>}
                            </td>
                            <td style={{ textAlign: 'right' }} className="amount">{r.revenue ? r.revenue.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—'}</td>
                            <td style={{ textAlign: 'right' }} className="amount">{r.cost ? r.cost.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—'}</td>
                            <td style={{ textAlign: 'right', fontWeight: 600, color: gp >= 0 ? 'var(--success)' : 'var(--danger)' }} className="amount">{gp.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                            <td style={{ textAlign: 'right' }} className="amount">{r.revenue > 0 ? `${((gp / r.revenue) * 100).toFixed(1)}%` : '—'}</td>
                          </tr>
                        );
                      })}
                      {!pnlData.rows.length && <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 16 }}>No bookings yet</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {cancelReview && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="card" style={{ width: 640, maxHeight: '85vh', overflowY: 'auto', padding: 28 }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Cancel Package — {pkg.package_code}</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
              This will cancel {cancelReview.bookings.length} active booking{cancelReview.bookings.length === 1 ? '' : 's'} and void any invoice generated from them. Nothing is deleted — everything is reversed with a full audit trail.
            </div>

            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label">Cancellation Reason *</label>
              <input className="form-input" value={cancelReason} onChange={e => setCancelReason(e.target.value)} placeholder="Reason for cancelling this package..." />
            </div>

            {cancelReview.supplierWriteoffs.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Supplier Advance Write-off</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>Unconsumed advance is written off as a loss — this is automatic, not a choice.</div>
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                  <thead><tr style={{ borderBottom: '1px solid var(--border)', color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>
                    <th style={{ textAlign: 'left', padding: '4px 0' }}>Supplier</th>
                    <th style={{ textAlign: 'right', padding: '4px 0' }}>Remaining Deposit (LKR)</th>
                  </tr></thead>
                  <tbody>
                    {cancelReview.supplierWriteoffs.map(w => (
                      <tr key={w.supplier_id}>
                        <td style={{ padding: '4px 0' }}>{w.supplier_name}</td>
                        <td style={{ padding: '4px 0', textAlign: 'right', color: 'var(--danger)', fontWeight: 600 }}>{w.remaining_deposit.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {Object.keys(cancelReview.clientRows).length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Client Deposit Disposition</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>For each client, split their deposit balance between refund and forfeit. Any remainder stays as unapplied credit.</div>
                {Object.entries(cancelReview.clientRows).map(([clientId, row]) => {
                  const refund = parseFloat(row.refund) || 0;
                  const forfeit = parseFloat(row.forfeit) || 0;
                  const remainder = row.balance - refund - forfeit;
                  const invalidRow = refund < 0 || forfeit < 0 || (refund + forfeit) > row.balance + 0.01;
                  return (
                    <div key={clientId} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontWeight: 600 }}>{row.client_name}</span>
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Balance: LKR {row.balance.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                      </div>
                      {row.balance <= 0 ? (
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No deposit on file</div>
                      ) : (
                        <>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                            <div className="form-group" style={{ margin: 0 }}>
                              <label className="form-label" style={{ fontSize: 11 }}>Refund</label>
                              <input className="form-input" type="number" step="0.01" min="0" value={row.refund}
                                onChange={e => setCancelReview(r => ({ ...r, clientRows: { ...r.clientRows, [clientId]: { ...r.clientRows[clientId], refund: e.target.value } } }))} />
                            </div>
                            <div className="form-group" style={{ margin: 0 }}>
                              <label className="form-label" style={{ fontSize: 11 }}>Forfeit (customer's fault)</label>
                              <input className="form-input" type="number" step="0.01" min="0" value={row.forfeit}
                                onChange={e => setCancelReview(r => ({ ...r, clientRows: { ...r.clientRows, [clientId]: { ...r.clientRows[clientId], forfeit: e.target.value } } }))} />
                            </div>
                          </div>
                          <div style={{ fontSize: 12, marginTop: 6, color: invalidRow ? 'var(--danger)' : 'var(--text-muted)' }}>
                            {invalidRow ? 'Refund + forfeit cannot exceed the balance' : `Remaining as credit: LKR ${remainder.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {cancelReviewHasRefund() && (
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label className="form-label">Refund From</label>
                <select className="form-input" value={refundFromAccountId} onChange={e => setRefundFromAccountId(e.target.value)}>
                  <option value="">— Select account —</option>
                  {paymentAccounts.map(a => <option key={a.id} value={a.id}>{a.account_code} — {a.account_name}</option>)}
                </select>
              </div>
            )}

            {error && <div style={{ color: 'var(--danger)', marginBottom: 12, fontSize: 13 }}>{error}</div>}

            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setCancelReview(null)} disabled={cancelSaving}>Back</button>
              <button className="btn" style={{ flex: 1, background: 'var(--danger)', color: '#fff' }} onClick={confirmCancelPackage} disabled={cancelSaving || cancelReviewInvalid()}>
                {cancelSaving ? 'Cancelling…' : 'Confirm Cancellation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
